import { AfterViewInit, Component, ComponentRef, createComponent, ElementRef, EnvironmentInjector, Inject, Input, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { InlineToastService } from 'shared';
import * as L from 'leaflet';
import moment from 'moment';
import { Subscription } from 'rxjs';
import {
    ISupplierRegionsService,
    SUPPLIER_REGIONS_SERVICE_IMPL,
} from 'dm-src/services/suppliers/isupplier-regions-service';
import { DeliveryPlan } from 'dm-src/models/delivery-plan';
import {
    CourierLegendItem,
    DeliveryPlanViewerOrder,
    DeliveryPlanViewerRoutePlan,
    DeliveryPlanViewerRouteStop,
    DeliveryPlanViewerShift,
    DeliveryPlanViewerUnassignedOrder,
    DetailsRow,
    StopSelection,
} from './delivery-plan-viewer.models';
import {
    COORDINATE_DECIMALS,
    COURIER_ID_LABEL_LENGTH,
    COURIER_ROUTE_COLORS,
    DATE_FORMAT,
    DELIVERY_COLOR,
    DELIVERY_PLAN_TYPE,
    DETAILS_DIALOG_BACKDROP_CLASS,
    DETAILS_DIALOG_WINDOW_CLASS,
    MAP_FIT_MAX_ZOOM,
    MAP_FIT_PADDING_PX,
    MAP_FIT_ZOOM_OUT_LEVELS,
    MAP_INITIAL_CENTER,
    MAP_INITIAL_ZOOM,
    MAP_MAX_ZOOM,
    MAP_ATTRIBUTION_PREFIX,
    MAP_TILE_ATTRIBUTION,
    MAP_TILE_KEEP_BUFFER,
    MAP_TILE_UPDATE_INTERVAL_MS,
    MAP_TILE_URL,
    MAP_VECTOR_RENDERER_PADDING,
    MARKER_ICON_ANCHOR,
    MARKER_ICON_SIZE,
    MOBILE_MEDIA_QUERY,
    PICKUP_COLOR,
    ROUTE_LINE_WEIGHT,
    SELECTED_MARKER_Z_INDEX_OFFSET,
    SHEET_COLLAPSED_SIZE,
    STOP_TYPE_DELIVERY,
    STOP_TYPE_PICKUP,
    STOPS_PANEL_WIDTH_PX,
    TIME_FORMAT,
    TOAST_ERROR_DURATION_MS,
    UNASSIGNED_COLOR,
} from './delivery-plan-viewer.constants';
import { DeliveryPlanViewerStopMarkerComponent } from './stop-marker/delivery-plan-viewer-stop-marker.component';

interface RenderedStopMarker
{
    stop: DeliveryPlanViewerRouteStop;
    marker: L.Marker;
    componentRef: ComponentRef<DeliveryPlanViewerStopMarkerComponent>;
}

interface RenderedUnassignedMarker
{
    order: DeliveryPlanViewerUnassignedOrder;
    marker: L.Marker;
    componentRef: ComponentRef<DeliveryPlanViewerStopMarkerComponent>;
}

@Component({
    selector: 'app-delivery-plan-viewer',
    templateUrl: './delivery-plan-viewer.component.html',
    styleUrls: ['./delivery-plan-viewer.component.scss'],
    providers: [InlineToastService]
})
export class DeliveryPlanViewerComponent implements OnInit, AfterViewInit, OnDestroy
{
    @Input() public regionGuid: string;
    @Input() public supplierGuid: string | null = null;

    @ViewChild("mapContainer", { static: true })
    private mapContainer: ElementRef<HTMLDivElement>;

    @ViewChild("orderDetailsModal", { static: true })
    private orderDetailsModal: TemplateRef<unknown>;

    public detailsOrderCode = "";

    public detailsRows: DetailsRow[] = [];

    public orders: DeliveryPlanViewerOrder[] = [];
    public visibleOrders: DeliveryPlanViewerOrder[] = [];
    public routes: DeliveryPlanViewerRoutePlan[] = [];
    public unassignedOrders: DeliveryPlanViewerUnassignedOrder[] = [];

    public showUnassignedOnMap = true;

    public courierNames: Record<string, string> = {};

    public legendItems: CourierLegendItem[] = [];

    public filteredCourierId: string | null = null;
    public selectedOrderCode: string | null = null;
    public selectedStopId: string | null = null;

    public deliveryDate: Date = new Date();

    public isLoadingPlan = false;

    public hasNoPlan = false;
    public isPlanLoaded = false;

    public isMobile = false;

    private map: L.Map | null = null;
    private mapLayers: L.LayerGroup | null = null;

    private stopMarkers = new Map<string, RenderedStopMarker>();
    private unassignedMarkers = new Map<string, RenderedUnassignedMarker>();

    private resizeObserver: ResizeObserver | null = null;

    private planSubscription: Subscription | null = null;

    /** Watches the window width for isMobile. */
    private breakpointSubscription: Subscription | null = null;

    constructor(
        @Inject(SUPPLIER_REGIONS_SERVICE_IMPL)
        private supplierRegionsService: ISupplierRegionsService,
        private toast: InlineToastService,
        private modalService: NgbModal,
        private zone: NgZone,
        private environmentInjector: EnvironmentInjector,
        private breakpointObserver: BreakpointObserver
    ) {}

    public get isCourierMode(): boolean
    {
        return !!this.supplierGuid;
    }

    public get showCourierNoRoute(): boolean
    {
        return (
            this.isCourierMode &&
            !this.isLoadingPlan &&
            (this.hasNoPlan || (this.isPlanLoaded && this.filteredRouteIndex < 0))
        );
    }

    public get unassignedWithLocationCount(): number
    {
        return this.unassignedOrders.filter((order) => this.hasLocation(order)).length;
    }

    public get canToggleUnassignedOnMap(): boolean
    {
        return (
            !this.isCourierMode &&
            this.filteredCourierId == null &&
            this.unassignedWithLocationCount > 0
        );
    }

    public get filteredRouteIndex(): number
    {
        return this.filteredCourierId == null
            ? -1
            : this.routes.findIndex((route) => route.courierId === this.filteredCourierId);
    }

    public ngOnInit(): void
    {
        if (this.isCourierMode)
            this.filteredCourierId = this.supplierGuid;

        this.breakpointSubscription = this.breakpointObserver
            .observe(MOBILE_MEDIA_QUERY)
            .subscribe((state) => (this.isMobile = state.matches));

        this.fetchDeliveryPlan();
    }

    public ngAfterViewInit(): void
    {
        this.initMap();
    }

    public ngOnDestroy(): void
    {
        this.planSubscription?.unsubscribe();
        this.breakpointSubscription?.unsubscribe();
        this.resizeObserver?.disconnect();
        this.destroyStopMarkers();
        this.map?.remove();
        this.map = null;
    }

    public formatDate(date: Date): string
    {
        return moment(date).format(DATE_FORMAT);
    }

    public formatTime(date: Date | null): string
    {
        return date ? moment(date).format(TIME_FORMAT) : '';
    }

    public courierLabel(courierId: string): string
    {
        if (this.courierNames[courierId])
            return this.courierNames[courierId];

        return courierId.length > COURIER_ID_LABEL_LENGTH
            ? `${courierId.substring(0, COURIER_ID_LABEL_LENGTH)}...`
            : courierId;
    }

    public courierColor(routeIndex: number): string
    {
        return COURIER_ROUTE_COLORS[routeIndex % COURIER_ROUTE_COLORS.length];
    }

    public zoomIn(): void
    {
        this.map?.zoomIn();
    }

    public zoomOut(): void
    {
        this.map?.zoomOut();
    }

    public selectStop(stopId: string | null, lat?: number, lng?: number): void
    {
        this.selectedStopId = stopId;
        this.selectedOrderCode = null;
        this.panTo(lat, lng);
        this.updateMarkerSelection();
    }

    public selectOrder(orderCode: string | null, lat?: number, lng?: number): void
    {
        this.selectedOrderCode = orderCode;
        this.selectedStopId = null;
        this.panTo(lat, lng);
        this.updateMarkerSelection();
    }

    public onOrderSelected(order: DeliveryPlanViewerOrder | null): void
    {
        this.selectOrder(order?.code ?? null, order?.lat, order?.lng);
    }

    public onStopSelected(selection: StopSelection | null): void
    {
        this.selectStop(selection?.stopId ?? null, selection?.lat, selection?.lng);
    }

    public onUnassignedOrderSelected(order: DeliveryPlanViewerUnassignedOrder | null): void
    {
        this.selectOrder(order?.code ?? null, order?.lat ?? undefined, order?.lng ?? undefined);
    }

    public toggleUnassignedOnMap(): void
    {
        if (!this.canToggleUnassignedOnMap)
            return;

        this.showUnassignedOnMap = !this.showUnassignedOnMap;
        this.renderMapLayers();
    }

    public setCourierFilter(courierId: string | null): void
    {
        if (this.isCourierMode)
            return;

        this.filteredCourierId = courierId;
        this.refreshVisibleOrders();
        this.renderMapLayers();
    }

    public changeDate(deltaDays: number): void
    {
        this.setDeliveryDate(moment(this.deliveryDate).add(deltaDays, 'days').toDate());
    }

    public setDeliveryDate(date: Date): void
    {
        this.deliveryDate = date;
        this.clearPlanData();
        this.fetchDeliveryPlan();
    }

    public deleteOrder(code: string): void
    {
        this.orders = this.orders.filter((order) => order.code !== code);
        this.refreshVisibleOrders();
    }

    public fetchDeliveryPlan(): void
    {
        this.planSubscription?.unsubscribe();
        this.isLoadingPlan = true;
        this.hasNoPlan = false;
        this.isPlanLoaded = false;

        this.planSubscription = this.supplierRegionsService
            .getDeliveryPlan(
                this.regionGuid,
                this.formatDate(this.deliveryDate),
                DELIVERY_PLAN_TYPE,
                this.isCourierMode ? this.supplierGuid : undefined
            )
            .subscribe({
                next: (response) =>
                {
                    this.isLoadingPlan = false;
                    if (response.status === 200 && response.body)
                        this.processDeliveryPlan(response.body);
                    else if (response.status === 204)
                        this.hasNoPlan = true;
                    else
                    {
                        console.error("Delivery plan request returned an unexpected status", response.status);
                        this.showLoadError();
                    }
                },
                error: (error) =>
                {
                    this.isLoadingPlan = false;
                    console.error("Delivery plan request failed", error);
                    this.showLoadError();
                },
            });
    }

    public showLoadError(): void
    {
        this.toast.show("delivery-plan-viewer.load-error", undefined, TOAST_ERROR_DURATION_MS);
    }

    private refreshVisibleOrders(): void
    {
        if (this.filteredCourierId == null)
        {
            this.visibleOrders = this.orders;
            return;
        }

        const route = this.routes.find((r) => r.courierId === this.filteredCourierId);
        const courierOrderCodes = new Set(route?.stops.map((stop) => stop.orderCode) ?? []);
        this.visibleOrders = this.orders.filter((order) => courierOrderCodes.has(order.code));
    }

    private clearPlanData(): void
    {
        this.orders = [];
        this.visibleOrders = [];
        this.routes = [];
        this.unassignedOrders = [];
        this.courierNames = {};
        this.legendItems = [];

        if (!this.isCourierMode)
            this.filteredCourierId = null;

        this.selectedOrderCode = null;
        this.selectedStopId = null;
        this.hasNoPlan = false;
        this.isPlanLoaded = false;
        this.renderMapLayers();
    }

    private orderByCode(code: string): DeliveryPlanViewerOrder | undefined
    {
        return this.orders.find((order) => order.code === code);
    }

    private isPickup(stop: DeliveryPlanViewerRouteStop): boolean
    {
        return stop.stopType.toLowerCase() === STOP_TYPE_PICKUP;
    }

    private hasCoordinates(stop: DeliveryPlanViewerRouteStop): boolean
    {
        return stop.lat !== 0 || stop.lng !== 0;
    }

    private hasLocation(order: DeliveryPlanViewerUnassignedOrder): boolean
    {
        return order.lat != null && order.lng != null;
    }

    private buildUnassignedOrders(data: DeliveryPlan): DeliveryPlanViewerUnassignedOrder[]
    {
        const detailsByCode = new Map(
            (data.unassignedOrderDetails ?? []).map((details) => [
                (details.orderCode ?? '').toString().toLowerCase(),
                details,
            ])
        );

        return (data.unassignedOrderCodes ?? []).map((rawCode) => {
            const code = rawCode.toString();
            const details = detailsByCode.get(code.toLowerCase());
            const hasLocation = !!details && (details.lat !== 0 || details.lng !== 0);
            return {
                code,
                lat: hasLocation ? details.lat : null,
                lng: hasLocation ? details.lng : null,
            };
        });
    }

    private panTo(lat?: number, lng?: number): void
    {
        if (this.map && lat != null && lng != null)
            this.map.setView([lat, lng], this.map.getZoom());
    }

    /**
     * - route of a courier = every stop of every shift, in API order;
     * - order = every order code with a Delivery stop; its location and delivery ETA come from
     *   its first Delivery stop, its pickup ETA from its (last) Pickup stop, its courier name
     *   from the courier whose route has it;
     * - unassigned orders = the codes of `unassignedOrderCodes`, located with
     *   `unassignedOrderDetails` where possible.
     */
    private processDeliveryPlan(data: DeliveryPlan): void
    {
        const routes: DeliveryPlanViewerRoutePlan[] = [];
        const courierNames: Record<string, string> = {};
        const deliveryStopByCode = new Map<string, DeliveryPlanViewerRouteStop>();
        const pickupEtaByCode = new Map<string, Date>();
        const courierIdByCode = new Map<string, string>();

        for (const courier of data.couriers ?? [])
        {
            const courierId = (courier.courierId ?? '').toString();
            const courierName = (courier.courierName ?? '').toString();

            if (courierName)
                courierNames[courierId] = courierName;

            const stops: DeliveryPlanViewerRouteStop[] = [];
            const shifts: DeliveryPlanViewerShift[] = [];

            for (const shift of courier.shifts ?? [])
            {
                const shiftStops: DeliveryPlanViewerRouteStop[] = [];

                for (const stop of shift.routeStops ?? [])
                {
                    const routeStop: DeliveryPlanViewerRouteStop = {
                        id: (stop.id ?? '').toString(),
                        orderId: stop.orderId ?? 0,
                        orderCode: stop.orderCode ?? '',
                        stopType: stop.type ?? '',
                        eta: this.parseDate(stop.eta),
                        lat: stop.lat ?? 0,
                        lng: stop.lng ?? 0,
                        stopNumber: stops.length + 1,
                    };

                    stops.push(routeStop);
                    shiftStops.push(routeStop);

                    courierIdByCode.set(routeStop.orderCode, courierId);

                    const stopType = routeStop.stopType.toLowerCase();
                    if (stopType === STOP_TYPE_PICKUP)
                        pickupEtaByCode.set(routeStop.orderCode, routeStop.eta);
                    else if (stopType === STOP_TYPE_DELIVERY && !deliveryStopByCode.has(routeStop.orderCode))
                        deliveryStopByCode.set(routeStop.orderCode, routeStop);
                }

                shifts.push({
                    id: (shift.id ?? '').toString(),
                    start: this.parseDate(shift.start),
                    end: this.parseDate(shift.end),
                    stops: shiftStops
                });
            }

            routes.push({ courierId, stops, shifts });
        }

        const orders: DeliveryPlanViewerOrder[] = [];
        deliveryStopByCode.forEach((deliveryStop, code) => {
            orders.push({
                code,
                address: '',
                lat: deliveryStop.lat,
                lng: deliveryStop.lng,
                deliveryTimeSlot: '',
                courierName: courierNames[courierIdByCode.get(code)] ?? null,
                pickupEta: pickupEtaByCode.get(code) ?? null,
                deliveryEta: deliveryStop.eta
            });
        });

        this.routes = routes;
        this.courierNames = courierNames;
        this.orders = orders;
        this.unassignedOrders = this.buildUnassignedOrders(data);

        this.legendItems = routes.map((route, routeIndex) => ({
            courierId: route.courierId,
            label: this.courierLabel(route.courierId),
            color: this.courierColor(routeIndex),
        }));

        this.refreshVisibleOrders();
        this.renderMapLayers();
        this.fitToVisibleStops();
        this.isPlanLoaded = true;
    }

    /**
     * Fits the map to every marker on it, stop and unassigned (MAP_FIT_PADDING_PX around them, at most zoom
     * MAP_FIT_MAX_ZOOM), then steps back MAP_FIT_ZOOM_OUT_LEVELS levels. When the stops panel is
     * shown, the room it covers is left free too: its width on the right on desktop, the
     * collapsed sheet's height at the bottom on mobile. No markers = the map is left as it is.
     */
    private fitToVisibleStops(): void
    {
        const map = this.map;

        if (!map || this.stopMarkers.size + this.unassignedMarkers.size === 0)
            return;

        const points = [
            ...[...this.stopMarkers.values()].map(({ stop }) => L.latLng(stop.lat, stop.lng)),
            ...[...this.unassignedMarkers.values()].map(({ order }) =>
                L.latLng(order.lat, order.lng)
            ),
        ];
        const hasStopsPanel = this.filteredRouteIndex >= 0;

        const rightPadding = MAP_FIT_PADDING_PX + (hasStopsPanel && !this.isMobile ? STOPS_PANEL_WIDTH_PX : 0);

        const bottomPadding =
            MAP_FIT_PADDING_PX +
            (hasStopsPanel && this.isMobile
                ? this.mapContainer.nativeElement.clientHeight * SHEET_COLLAPSED_SIZE
                : 0);

        this.zone.runOutsideAngular(() =>
        {
            map.invalidateSize();
            map.fitBounds(L.latLngBounds(points), {
                paddingTopLeft: [MAP_FIT_PADDING_PX, MAP_FIT_PADDING_PX],
                paddingBottomRight: [rightPadding, bottomPadding],
                maxZoom: MAP_FIT_MAX_ZOOM,
                animate: false
            });

            if (MAP_FIT_ZOOM_OUT_LEVELS > 0)
                map.setZoom(map.getZoom() - MAP_FIT_ZOOM_OUT_LEVELS, { animate: false });
        });
    }

    private parseDate(value: string | null | undefined): Date
    {
        const date = value ? new Date(value) : null;
        return date && !isNaN(date.getTime()) ? date : new Date();
    }

    private initMap(): void
    {
        this.zone.runOutsideAngular(() =>
        {
            this.map = L.map(this.mapContainer.nativeElement, {
                center: MAP_INITIAL_CENTER,
                zoom: MAP_INITIAL_ZOOM,
                zoomControl: false,
                // SVG instead of canvas: a canvas big enough for MAP_VECTOR_RENDERER_PADDING costs a
                // lot of memory, while the SVG cost only depends on the number of lines.
                renderer: L.svg({ padding: MAP_VECTOR_RENDERER_PADDING })
            });

            this.map.attributionControl.setPrefix(MAP_ATTRIBUTION_PREFIX);

            L.tileLayer(MAP_TILE_URL, {
                maxZoom: MAP_MAX_ZOOM,
                attribution: MAP_TILE_ATTRIBUTION,
                keepBuffer: MAP_TILE_KEEP_BUFFER,
                updateInterval: MAP_TILE_UPDATE_INTERVAL_MS
            }).addTo(this.map);

            this.mapLayers = L.layerGroup().addTo(this.map);

            // The map is created while the modal is still animating, so its size has to be
            // recalculated whenever the container is resized.
            this.resizeObserver = new ResizeObserver(() => this.map?.invalidateSize());
            this.resizeObserver.observe(this.mapContainer.nativeElement);
        });

        // The plan may have arrived before the view was ready.
        this.renderMapLayers();
    }

    /**
     * Removes and rebuilds every route line, stop marker and unassigned marker from the current
     * plan, courier filter and unassigned toggle. Called when one of them changes; a selection
     * change only needs updateMarkerSelection().
     */
    private renderMapLayers(): void
    {
        const layers = this.mapLayers;

        if (!layers)
            return;

        this.zone.runOutsideAngular(() =>
        {
            layers.clearLayers();
            this.destroyStopMarkers();
            this.buildPolylines().forEach((polyline) => polyline.addTo(layers));
            this.buildMarkers().forEach((rendered) =>
            {
                rendered.marker.addTo(layers);
                this.stopMarkers.set(rendered.stop.id, rendered);
            });
            this.buildUnassignedMarkers().forEach((rendered) =>
            {
                rendered.marker.addTo(layers);
                this.unassignedMarkers.set(rendered.order.code, rendered);
            });
        });
    }

    /** Re-renders only the markers whose selected state changed instead of rebuilding all of them. */
    private updateMarkerSelection(): void
    {
        this.zone.runOutsideAngular(() =>
        {
            this.stopMarkers.forEach(({ stop, marker, componentRef }) =>
            {
                const isSelected = this.isStopSelected(stop);

                if (componentRef.instance.selected === isSelected)
                    return;

                componentRef.setInput('selected', isSelected);
                componentRef.changeDetectorRef.detectChanges();
                marker.setZIndexOffset(isSelected ? SELECTED_MARKER_Z_INDEX_OFFSET : 0);
            });

            this.unassignedMarkers.forEach(({ order, marker, componentRef }) =>
            {
                const isSelected = this.isUnassignedOrderSelected(order);

                if (componentRef.instance.selected === isSelected)
                    return;

                componentRef.setInput('selected', isSelected);
                componentRef.changeDetectorRef.detectChanges();
                marker.setZIndexOffset(isSelected ? SELECTED_MARKER_Z_INDEX_OFFSET : 0);
            });
        });
    }

    private isUnassignedOrderSelected(order: DeliveryPlanViewerUnassignedOrder): boolean
    {
        return this.selectedStopId == null && order.code === this.selectedOrderCode;
    }

    private isStopSelected(stop: DeliveryPlanViewerRouteStop): boolean
    {
        return (
            stop.id === this.selectedStopId ||
            (this.selectedStopId == null && stop.orderCode === this.selectedOrderCode)
        );
    }

    /**
     * One line per courier in the courier's color, ROUTE_LINE_WEIGHT px wide, through the stops
     * in API order. Without a courier filter it connects only the Delivery stops of every
     * courier; with a filter every stop (pickups too) of the filtered courier only. Stops at
     * (0, 0) are skipped; a courier with no drawable stop gets no line.
     */
    private buildPolylines(): L.Polyline[]
    {
        const polylines: L.Polyline[] = [];

        this.routes.forEach((route, routeIndex) =>
        {
            if (this.filteredCourierId != null && route.courierId !== this.filteredCourierId)
                return;

            const points = route.stops
                .filter((stop) => this.filteredCourierId != null || !this.isPickup(stop))
                .filter((stop) => this.hasCoordinates(stop))
                .map((stop) => L.latLng(stop.lat, stop.lng));

            if (points.length > 0)
            {
                polylines.push(
                    L.polyline(points, {
                        color: this.courierColor(routeIndex),
                        weight: ROUTE_LINE_WEIGHT
                    })
                );
            }
        });

        return polylines;
    }

    /**
     * One marker per stop: badge "<n>P" (pickup, PICKUP_COLOR) or "<n>D" (delivery,
     * DELIVERY_COLOR) where n is the stop's stopNumber (its position in the courier's route, API
     * order, from 1; the same number as in the stops panel), with the order code and "ETA: HH:mm"
     * below. Without a filter the numbers have gaps (the hidden pickups) but keep the route order.
     * Without a courier filter only Delivery stops
     * get markers; with a filter every stop of the filtered courier. Stops at (0, 0) are skipped.
     */
    private buildMarkers(): RenderedStopMarker[]
    {
        const markers: RenderedStopMarker[] = [];

        this.routes.forEach((route) =>
        {
            if (this.filteredCourierId != null && route.courierId !== this.filteredCourierId)
                return;

            route.stops.forEach((stop) =>
            {
                const isPickup = this.isPickup(stop);

                if (this.filteredCourierId == null && isPickup)
                    return;

                if (!this.hasCoordinates(stop))
                    return;

                const isSelected = this.isStopSelected(stop);

                const componentRef = this.createStopMarker({
                    stopNumber: stop.stopNumber,
                    isPickup,
                    color: isPickup ? PICKUP_COLOR : DELIVERY_COLOR,
                    orderCode: stop.orderCode,
                    eta: this.formatTime(stop.eta),
                    selected: isSelected
                });

                const marker = L.marker([stop.lat, stop.lng], {
                    icon: L.divIcon({
                        html: componentRef.location.nativeElement,
                        className: 'delivery-plan-viewer-stop-marker-icon',
                        iconSize: MARKER_ICON_SIZE,
                        iconAnchor: MARKER_ICON_ANCHOR,
                    }),
                    zIndexOffset: isSelected ? SELECTED_MARKER_Z_INDEX_OFFSET : 0,
                });

                marker.on('click', () =>
                    this.zone.run(() =>
                    {
                        this.selectStop(stop.id);
                        this.showOrderDetailsDialog(
                            stop.orderCode,
                            this.orderByCode(stop.orderCode),
                            stop,
                            route.courierId
                        );
                    })
                );

                markers.push({ stop, marker, componentRef });
            });
        });

        return markers;
    }

    /** One grey marker per unassigned order with a location */
    private buildUnassignedMarkers(): RenderedUnassignedMarker[]
    {
        if (!this.showUnassignedOnMap || this.filteredCourierId != null)
            return [];

        return this.unassignedOrders
            .filter((order) => this.hasLocation(order))
            .map((order) =>
            {
                const isSelected = this.isUnassignedOrderSelected(order);

                const componentRef = this.createStopMarker({
                    isUnassigned: true,
                    color: UNASSIGNED_COLOR,
                    orderCode: order.code,
                    selected: isSelected
                });

                const marker = L.marker([order.lat, order.lng], {
                    icon: L.divIcon({
                        html: componentRef.location.nativeElement,
                        className: 'delivery-plan-viewer-stop-marker-icon',
                        iconSize: MARKER_ICON_SIZE,
                        iconAnchor: MARKER_ICON_ANCHOR
                    }),
                    zIndexOffset: isSelected ? SELECTED_MARKER_Z_INDEX_OFFSET : 0,
                });

                marker.on('click', () =>
                    this.zone.run(() =>
                    {
                        this.selectOrder(order.code);
                        this.showUnassignedOrderDetailsDialog(order);
                    })
                );

                return { order, marker, componentRef };
            });
    }

    private showUnassignedOrderDetailsDialog(order: DeliveryPlanViewerUnassignedOrder): void
    {
        this.detailsOrderCode = order.code;
        this.detailsRows = [
            { label: "delivery-plan-viewer.details-order-code", value: order.code },
            {
                label: "delivery-plan-viewer.details-courier",
                value: "delivery-plan-viewer.unassigned",
                translateValue: true
            },
            {
                label: "common.coordinates",
                value: `${order.lat.toFixed(COORDINATE_DECIMALS)}, ${order.lng.toFixed(COORDINATE_DECIMALS)}`
            }
        ];
        this.openDetailsDialog();
    }

    private showOrderDetailsDialog(orderCode: string, order?: DeliveryPlanViewerOrder, stop?: DeliveryPlanViewerRouteStop, courierId?: string
    ): void
    {
        const courierName = courierId != null ? this.courierLabel(courierId) : order?.courierName;
        const lat = stop?.lat ?? order?.lat;
        const lng = stop?.lng ?? order?.lng;

        const rows: DetailsRow[] = [{ label: "delivery-plan-viewer.details-order-code", value: orderCode }];
        if (order?.address)
            rows.push({ label: "delivery-plan-viewer.details-address", value: order.address });
        if (courierName)
            rows.push({ label: "delivery-plan-viewer.details-courier", value: courierName });

        if (stop)
        {
            rows.push({
                label: "delivery-plan-viewer.details-stop-type",
                value: this.isPickup(stop)
                    ? "delivery-plan-viewer.stop-type-pickup"
                    : "delivery-plan-viewer.stop-type-delivery",
                translateValue: true,
            });
            rows.push({ label: "delivery-plan-viewer.details-stop-eta", value: this.formatTime(stop.eta) });
        }
        if (order?.pickupEta)
            rows.push({ label: "delivery-plan-viewer.details-pickup-eta", value: this.formatTime(order.pickupEta) });

        if (order?.deliveryEta)
            rows.push({ label: "delivery-plan-viewer.details-delivery-eta", value: this.formatTime(order.deliveryEta) });

        if (order?.deliveryTimeSlot)
            rows.push({ label: "delivery-plan-viewer.details-time-slot", value: order.deliveryTimeSlot });

        if (lat != null && lng != null)
            rows.push({
                label: "common.coordinates",
                value: `${lat.toFixed(COORDINATE_DECIMALS)}, ${lng.toFixed(COORDINATE_DECIMALS)}`,
            });

        this.detailsOrderCode = orderCode;
        this.detailsRows = rows;
        this.openDetailsDialog();
    }

    private openDetailsDialog(): void
    {
        this.modalService.open(this.orderDetailsModal, {
            size: 'sm',
            centered: true,
            backdropClass: DETAILS_DIALOG_BACKDROP_CLASS,
            windowClass: DETAILS_DIALOG_WINDOW_CLASS
        });
    }

    /**
     * Creates the component that renders one marker's look. Marker components live outside any
     * Angular view, so they only re-render on an explicit detectChanges(): once here, and in
     * updateMarkerSelection() when their selection changes.
     */
    private createStopMarker(
        inputs: Partial<
            Pick<DeliveryPlanViewerStopMarkerComponent,
                'stopNumber' | 'isPickup' | 'isUnassigned' | 'color' | 'orderCode' | 'eta' | 'selected'
            >
        >
    ): ComponentRef<DeliveryPlanViewerStopMarkerComponent>
    {
        const markerRef = createComponent(DeliveryPlanViewerStopMarkerComponent, {
            environmentInjector: this.environmentInjector
        });

        Object.entries(inputs).forEach(([name, value]) => markerRef.setInput(name, value));
        markerRef.changeDetectorRef.detectChanges();
        return markerRef;
    }

    private destroyStopMarkers(): void
    {
        this.stopMarkers.forEach(({ componentRef }) => componentRef.destroy());
        this.stopMarkers.clear();
        this.unassignedMarkers.forEach(({ componentRef }) => componentRef.destroy());
        this.unassignedMarkers.clear();
    }

}